import moment from 'moment';
import { FieldMetaState } from 'react-final-form';

export type FieldValidatorResult = string | undefined;

export type FieldValidator = (value: any) => FieldValidatorResult;

export const composeValidators =
    (...validators: any[]): any =>
    (value: any, record: unknown, meta?: FieldMetaState<any>): any =>
        validators.reduce(
            (error, validator) => error || validator(value, record, meta),
            undefined
        );

export const isExactlyDivisibleBy =
    (denominator: number): FieldValidator =>
    (value: any): FieldValidatorResult => {
        if (value) {
            const numberValue = Number(value);

            if (isNaN(numberValue)) {
                return 'Must be a number';
            }

            if (numberValue % denominator !== 0) {
                return denominator === 1
                    ? 'Must be a whole number'
                    : `Must be divisible by ${denominator}`;
            }
        }
        return undefined;
    };

export const isInteger: FieldValidator = isExactlyDivisibleBy(1);

export const isExactlyDivisibleByHalf: FieldValidator =
    isExactlyDivisibleBy(0.5);

export const isBoolean: FieldValidator = (value) =>
    typeof value === 'boolean' ? undefined : 'Required';

export const isRequired: FieldValidator = (value) =>
    value ? undefined : 'Required';

export const isRequiredCustom =
    (message: string): FieldValidator =>
    (value: any): FieldValidatorResult =>
        value ? undefined : message;

export const isRequiredNumber: FieldValidator = (value) =>
    !value && value !== 0 ? 'Required' : undefined;

export const isRequiredArray: FieldValidator = (
    value: any[] | undefined | null
) => (!value || !value.length ? 'Required' : undefined);

export const hasLength = (value: any): FieldValidatorResult =>
    value && value.length ? undefined : 'Required';

export const isNumber = (value: any): FieldValidatorResult =>
    value && isNaN(Number(value)) ? 'Must be a number' : undefined;

export const isPositiveNumber = (value: any): FieldValidatorResult => {
    if (value !== undefined && value !== null) {
        const numberValue = Number(value);
        if (isNaN(numberValue)) {
            return 'Must be a number';
        }
        if (numberValue <= 0) {
            return 'Must be a positive number';
        }
    }
    return undefined;
};

/**
 * Regex that matches numeric with up to 2 decimal places
 * {@link https://stackoverflow.com/questions/660682/regex-that-matches-numeric-with-up-to-2-decimal-places}
 */
export const noMoreThanTwoDecimalPlacesRegex = new RegExp(
    /^\s*-?(\d+(\.\d{1,2})?|\.\d{1,2})\s*$/
);

export const hasNoMoreThanTwoDecimalPlaces = (
    value: any
): FieldValidatorResult =>
    value && !noMoreThanTwoDecimalPlacesRegex.test(value)
        ? 'Must have no more than 2 decimal places'
        : undefined;

/**
 * Regex for validating decimal numbers
 * {@link https://stackoverflow.com/questions/10023845/regex-in-javascript-for-validating-decimal-numbers/36485704}
 */
export const decimalRegex = new RegExp(/^\d+(\.\d{1,2})?$/);

export const isNotIncompleteDecimal = (value: any): FieldValidatorResult =>
    value && !decimalRegex.test(value) ? 'Must be a valid number' : undefined;

export const pattern = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);

export const isEmail = (value: any): FieldValidatorResult => {
    return value && !pattern.test(value)
        ? 'Not a valid email address'
        : undefined;
};

export const mobileNumberRegex = new RegExp(/^[0][1-9]\d{6,10}$/);

export const isMobileNumber = (value: any): FieldValidatorResult =>
    value && !mobileNumberRegex.test(value)
        ? 'Not a valid mobile number (format should be 0212345678)'
        : undefined;

export const contactNumberRegex = new RegExp(
    /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{3})/
);

export const isPhoneNumber = (value: any): FieldValidatorResult =>
    value && !contactNumberRegex.test(value)
        ? 'Not a valid phone number'
        : undefined;

export const maxValue =
    (max: number): any =>
    (value: any): FieldValidatorResult =>
        value && value > max
            ? `Must be less than or equal to ${max}`
            : undefined;
export const maxValue60 = maxValue(60);
export const maxValue99 = maxValue(99.99);
export const maxValue100 = maxValue(100);
export const maxValue999 = maxValue(999.99);
export const maxValue999Half = maxValue(999.5);
export const maxValue1000 = maxValue(1000);
export const maxValue10000 = maxValue(10000);
export const maxValue100000 = maxValue(100000);
export const maxValue999999 = maxValue(999999.99);
export const maxValue1000000 = maxValue(1000000);

export const minValue =
    (min: number): any =>
    (value: any): FieldValidatorResult =>
        value && value < min
            ? `Must be greater than or equal to ${min}`
            : undefined;
export const minValue0 = minValue(0);

export const minLength =
    (length: number): any =>
    (value: string): FieldValidatorResult =>
        value && value.length < length
            ? `Minimum ${length} ${
                  length === 1 ? 'character' : 'characters'
              } required`
            : undefined;

export const maxLength =
    (length: number): any =>
    (value: string): FieldValidatorResult =>
        value && value.length > length
            ? `Maximum ${length} ${length === 1 ? 'character' : 'characters'}`
            : undefined;
export const minLength8 = minLength(8);
export const maxLength20 = maxLength(20);

export const orchardRpinRegex = new RegExp(/^[RK][0-9]{4}$/);

export const isOrchardRpin = (value: any): FieldValidatorResult =>
    value && !orchardRpinRegex.test(value)
        ? 'Not a valid orchard RPIN (e.g. R1234 or K1234)'
        : undefined;

export const isNotBeforeTwoYearsAgo = (value: any): FieldValidatorResult => {
    if (value) {
        const date = moment(value, 'YYYY-MM-DD');
        const twoYearsAgo = moment().subtract(2, 'year');
        if (date.isBefore(twoYearsAgo)) {
            return 'Must be less than 2 years ago';
        }
    }
    return undefined;
};

export const isTrimmed: FieldValidator = (
    value: string
): FieldValidatorResult =>
    value && value !== value.trim()
        ? 'Cannot have leading or trailing spaces'
        : undefined;

export const isValidTime: FieldValidator = (
    value: string
): FieldValidatorResult =>
    value && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)
        ? undefined
        : 'Must be a valid time within 24:00 eg 23:59';

export const isTime: FieldValidator = (value: string): FieldValidatorResult =>
    value && /\d{2}:\d{2}/.test(value)
        ? undefined
        : 'Must be a valid time format eg HH:MM';

export const isDuration: FieldValidator = (value: string): FieldValidatorResult =>
    value && /^\d{1,2}:\d{2}$/.test(value)
        ? undefined
        : 'Must be a valid duration format eg H:MM or HH:MM';

export const isDate: FieldValidator = (value: string): FieldValidatorResult =>
    value && /\d{4}-\d{2}-\d{2}/.test(value)
        ? undefined
        : 'Must be a valid time format eg YYYY-MM-DD';

// A more universal version of isDate supporting - and / delimiters
export const isDateMixed: FieldValidator = (
    value: string
): FieldValidatorResult =>
    value && /\d{4}(\/|-)\d{2}(\/|-)\d{2}/.test(value)
    ? undefined
    : 'Must be a valid time format eg YYYY-MM-DD or YYYY/MM/DD';

export const shortNameRegex = new RegExp(/^[A-Z0-9]{2}$/);

export const isShortName = (value: any): FieldValidatorResult =>
    value && !shortNameRegex.test(value)
        ? 'Must be 2 characters (A-Z0-9)'
        : undefined;

export default {
    composeValidators,
    isInteger,
    isBoolean,
    isRequired,
    isRequiredCustom,
    isRequiredNumber,
    isRequiredArray,
    hasLength,
    isNumber,
    isPositiveNumber,
    hasNoMoreThanTwoDecimalPlaces,
    isNotIncompleteDecimal,
    isEmail,
    isPhoneNumber,
    maxValue,
    maxValue60,
    maxValue99,
    maxValue100,
    maxValue999,
    maxValue999Half,
    maxValue999999,
    maxValue1000,
    maxValue10000,
    maxValue100000,
    maxValue1000000,
    minValue,
    minValue0,
    minLength8,
    maxLength20,
    isOrchardRpin,
    isNotBeforeTwoYearsAgo,
    isMobileNumber,
    isTrimmed,
    isExactlyDivisibleByHalf,
    isTime,
    isDuration,
    isDate,
    isDateMixed,
    isShortName
};
